import React, { useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { orderAPI, statusAPI } from '../services/api';
import ActionButton from './ActionButton';
import { getOrderColors, PRIORITY_COLORS, STATUS_COLORS } from '../utils/statusColors';

const OrderDetail = ({ order, onClose, onUpdate }) => {
  const { user } = useAuth();
  const { selectedShop } = useOutletContext();
  const navigate = useNavigate();
  
  const [isCancelling, setIsCancelling] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState(null);

  const colors = getOrderColors(order);
  const priorityColors = PRIORITY_COLORS[order.priority] || PRIORITY_COLORS['Normal'];
  const statusColors = STATUS_COLORS[order.status] || STATUS_COLORS['Pending'];

  const isCancelled = order.status === 'Cancelled';
  const isDelivered = order.status === 'Delivered';

  // Spec: "Total: show if at least one actual_price exists, blank if all null"
  const hasActualPrice = order.items?.some(i => i.actual_price !== null && i.actual_price !== undefined);
  const totalAmount = hasActualPrice ? order.total_amount : null;

  // Handles Cancellation (Updates Status to 'Cancelled' with optional reason)
  const handleCancelSubmit = async () => {
    try {
      setError(null);
      await statusAPI.updateStatus(order.order_id, selectedShop, { 
        status: 'Cancelled',
        cancellation_reason: cancelReason 
      });
      onUpdate();
      onClose();
    } catch (err) {
      setError('Failed to cancel order.');
    }
  };

  // Handles Admin Delete
  const handleDelete = async () => {
    if (!window.confirm(`Are you sure you want to PERMANENTLY DELETE order ${order.order_id}?`)) return;
    try {
      setIsDeleting(true);
      await orderAPI.delete(order.order_id, selectedShop);
      onUpdate();
      onClose();
    } catch (err) {
      setError('Failed to delete order. Ensure you have Admin permissions.');
      setIsDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end bg-black bg-opacity-60 transition-opacity" onClick={onClose}>
      
      {/* Bottom Sheet Container - Stop propagation so clicks inside don't close it */}
      <div 
        className={`bg-white w-full max-h-[90vh] rounded-t-3xl shadow-2xl flex flex-col overflow-hidden ${isCancelled ? 'opacity-95 grayscale-[0.3]' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        
        {/* Header Section */}
        <div className={`px-6 py-4 border-b ${colors.border} bg-gray-50 flex justify-between items-start rounded-t-3xl`}>
          <div>
            <h2 className="text-2xl font-black text-gray-900 tracking-tight">{order.order_id}</h2>
            <div className="mt-1.5 flex flex-wrap gap-2">
              <span className={`text-xs px-2.5 py-0.5 rounded-md font-bold ${statusColors.bg} ${statusColors.text} border ${statusColors.border}`}>
                {order.status}
              </span>
              {order.priority === 'Urgent' && (
                <span className={`text-xs px-2.5 py-0.5 rounded-md font-bold ${priorityColors.bg} ${priorityColors.text}`}>
                  🔴 URGENT
                </span>
              )}
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-800 p-2 bg-white rounded-full shadow-sm border border-gray-200">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        {/* Scrollable Content Section */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6 bg-white">
          
          {error && (
            <div className="bg-red-50 text-red-700 p-3 rounded-md text-sm font-medium border border-red-200">
              {error}
            </div>
          )}

          {isCancelled && order.cancellation_reason && (
            <div className="bg-gray-100 border-l-4 border-gray-400 p-3 text-sm text-gray-700 font-medium rounded">
              <span className="font-bold text-gray-900">Cancel Reason: </span>{order.cancellation_reason}
            </div>
          )}

          {/* Customer & Meta Information Grid */}
          <div className="grid grid-cols-2 gap-y-5 gap-x-4 text-sm">
            <div className="col-span-2 sm:col-span-1">
              <p className="text-gray-500 font-medium">Customer</p>
              <p className="font-bold text-gray-900 text-base">{order.customer_name}</p>
            </div>
            <div className="col-span-2 sm:col-span-1">
              <p className="text-gray-500 font-medium">Phone</p>
              <p className="font-bold text-gray-900 text-base">{order.phone_number}</p>
            </div>
            <div className="col-span-1">
              <p className="text-gray-500 font-medium">Pickup Date</p>
              <p className="font-bold text-gray-900">
                {new Date(order.pickup_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
              </p>
            </div>
            <div className="col-span-1">
              <p className="text-gray-500 font-medium">Target Date</p>
              <p className={`font-bold ${colors.text}`}>
                {new Date(order.target_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
              </p>
            </div>
            
            {order.customer_location && (
              <div className="col-span-2">
                <p className="text-gray-500 font-medium">Location</p>
                <p className="font-semibold text-gray-900">{order.customer_location}</p>
              </div>
            )}
            {order.assigned_to && (
            <div className="col-span-2">
                <p className="text-gray-500 font-medium">Assigned Worker</p>
                <p className="font-semibold text-gray-900">{order.assigned_to_name || 'Worker Assigned'}</p>
            </div>
            )}
            {order.notes && (
              <div className="col-span-2">
                <p className="text-gray-500 font-medium">Internal Notes</p>
                <p className="font-medium text-gray-800 bg-yellow-50 p-3 rounded-md border border-yellow-100">{order.notes}</p>
              </div>
            )}
          </div>

          {/* Items Line Display */}
          <div>
            <h3 className="font-bold text-gray-900 mb-3 border-b border-gray-200 pb-2">Order Items</h3>
            <div className="space-y-3">
              {order.items?.map((item, index) => (
                <div key={item.item_id || index} className="flex justify-between items-start bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <div className="pr-4 flex-1">
                    <span className="font-bold text-gray-800 text-sm">{index + 1}. {item.item_type}</span>
                    <p className="text-sm text-gray-600 mt-1 leading-relaxed">{item.issue_description}</p>
                  </div>
                  <div className="text-right text-sm min-w-[80px]">
                    <p className="text-gray-500">Est: <span className="font-medium">{item.estimated_price !== null ? `₹${item.estimated_price}` : '-'}</span></p>
                    <p className="text-gray-900 mt-1 font-bold">
                      Act: {item.actual_price !== null ? `₹${item.actual_price}` : '-'}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Dynamic Total Row */}
            {totalAmount !== null && (
              <div className="mt-4 flex justify-between items-center py-4 border-t-2 border-b-2 border-gray-100 bg-gray-50 px-4 rounded-lg font-black text-lg">
                <span className="text-gray-800">Final Total:</span>
                <span className="text-blue-700 text-xl">₹{totalAmount}</span>
              </div>
            )}
          </div>
        </div>

        {/* Floating Actions Footer */}
        <div className="p-4 border-t border-gray-200 bg-white pb-8">
          
          {!isCancelled ? (
            <div className="space-y-3">
              {/* Context Aware Button */}
              <div className="[&>div]:w-full [&>div>button]:w-full [&>div>button]:py-3 [&>div>button]:text-base">
                <ActionButton order={order} onUpdate={() => { onUpdate(); onClose(); }} />
              </div>

              <div className="flex space-x-3">
                <button 
                  onClick={() => navigate(`/edit-order/${order.order_id}`)}
                  className="flex-1 bg-white hover:bg-gray-50 text-gray-700 py-3 rounded-lg font-bold text-sm transition-colors border border-gray-300 shadow-sm"
                >
                  Edit Order
                </button>

                {!isDelivered && (
                  <button 
                    onClick={() => setIsCancelling(!isCancelling)}
                    className={`flex-1 ${isCancelling ? 'bg-gray-200 text-gray-800 border-gray-300' : 'bg-white hover:bg-red-50 text-red-600 border-red-200'} py-3 rounded-lg font-bold text-sm transition-colors border shadow-sm`}
                  >
                    {isCancelling ? 'Close Cancel' : 'Cancel Order'}
                  </button>
                )}
              </div>

              {/* Inline Cancel Drawer */}
              {isCancelling && (
                <div className="bg-red-50 p-4 rounded-xl border border-red-200 mt-3">
                  <p className="text-sm font-bold text-red-800 mb-2">Confirm Cancellation</p>
                  <textarea 
                    value={cancelReason}
                    onChange={(e) => setCancelReason(e.target.value)}
                    placeholder="Why is this order being cancelled? (Optional)"
                    className="w-full text-sm p-3 border border-red-300 rounded-lg focus:ring-red-500 focus:border-red-500 mb-3 bg-white"
                    rows="2"
                  ></textarea>
                  <button 
                    onClick={handleCancelSubmit}
                    className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-3 rounded-lg transition-colors shadow-sm"
                  >
                    Confirm & Cancel Order
                  </button>
                </div>
              )}

              {/* Admin-Only Delete */}
              {user?.role === 'Admin' && (
                <div className="pt-2">
                  <button 
                    onClick={handleDelete}
                    disabled={isDeleting}
                    className="w-full text-center text-sm font-semibold text-gray-400 hover:text-red-600 transition-colors underline decoration-gray-300 hover:decoration-red-600 py-2"
                  >
                    {isDeleting ? 'Deleting...' : 'Delete Order (Admin Only)'}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-2">
              <span className="font-bold text-gray-500 uppercase tracking-widest text-sm">Order Cancelled</span>
            </div>
          )}
        </div>
        
      </div>
    </div>
  );
};

export default OrderDetail;